import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import PageLoader from '../../components/PageLoader';
import TablePagination from '../../components/TablePagination';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiCheckCircle, FiXCircle, FiClock, FiSearch, FiEye } from 'react-icons/fi';
import { admin, statusBadgeClass } from '../../components/admin/adminUi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminTransactions = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, [pagination.page, typeFilter, statusFilter]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (search) params.search = search;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await axios.get(`${API_URL}/admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setTransactions(response.data.transactions || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchTransactions();
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'type') {
      setTypeFilter(value);
    } else if (filterType === 'status') {
      setStatusFilter(value);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleViewDetails = (transaction) => {
    const serverId =
      typeof transaction.serverId === 'object' && transaction.serverId !== null
        ? transaction.serverId._id
        : transaction.serverId;

    if ((transaction.type === 'server_purchase' || transaction.type === 'server_renewal') && serverId) {
      navigate(`/server/${serverId}`);
    } else if (transaction.type === 'deposit') {
      const paymentUrl = transaction.paymentUrl;
      const trackId = transaction.oxapayTrackId || transaction.hoodpayTransactionId;

      if (paymentUrl) {
        window.open(paymentUrl, '_blank');
      } else if (trackId && trackId !== 'pending') {
        window.open(paymentUrl || '#', '_blank');
      } else {
        toast.error('Payment URL not available for this transaction');
      }
    } else {
      toast.success(`Transaction: ${transaction.description || 'N/A'}`, { duration: 5000 });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <FiCheckCircle className="text-[#4ADE80]" size={18} />;
      case 'failed':
      case 'expired':
        return <FiXCircle className="text-[#FCA5A5]" size={18} />;
      default:
        return <FiClock className="text-[#FBBF24]" size={18} />;
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`${admin.pageStretch} min-h-0`}>
        <div className="shrink-0">
          <h1 className={admin.title}>Transactions</h1>
          <p className={admin.subtitle}>Search and filter platform-wide payments.</p>
        </div>

        <div className={`${admin.card} ${admin.cardInner} shrink-0`}>
          <div className="grid gap-3 md:grid-cols-4 md:gap-4">
            <form onSubmit={handleSearch} className="md:col-span-2">
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by username or email…"
                  className={`${admin.input} pl-10`}
                />
                <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35" size={18} />
              </div>
            </form>

            <select
              value={typeFilter}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className={`${admin.select} text-white/90`}
            >
              <option value="">All types</option>
              <option value="deposit">Deposit</option>
              <option value="server_purchase">Server purchase</option>
              <option value="server_renewal">Server renewal</option>
              <option value="admin_adjustment">Admin adjustment</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="refund">Refund</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={`${admin.select} text-white/90`}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={handleSearch} className={admin.btnPrimary}>
              Search
            </button>
            {(search || typeFilter || statusFilter) && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setTypeFilter('');
                  setStatusFilter('');
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className={admin.btnSecondary}
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        <div className={`${admin.tableShell} min-h-0 flex-1`}>
          <div className={admin.tableScroll}>
            <table className={admin.table}>
            <thead className={admin.thead}>
              <tr>
                <th className={admin.th}>User</th>
                <th className={admin.th}>Type</th>
                <th className={admin.th}>Amount</th>
                <th className={admin.th}>Status</th>
                <th className={admin.th}>Description</th>
                <th className={admin.th}>Date</th>
                <th className={admin.th}>Actions</th>
              </tr>
            </thead>
            <tbody className={admin.tbody}>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className={`${admin.td} py-10 text-center ${admin.tdMuted}`}>
                    No transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr key={transaction._id} className={admin.tr}>
                    <td className={admin.td}>
                      <div>
                        <span className="font-medium text-white">
                          {transaction.userId?.username || 'N/A'}
                        </span>
                        {transaction.userId?.email && (
                          <div className="text-xs text-white/45">{transaction.userId.email}</div>
                        )}
                      </div>
                    </td>
                    <td className={`${admin.td} ${admin.tdMuted}`}>
                      {transaction.type.replace('_', ' ').toUpperCase()}
                    </td>
                    <td className={admin.td}>
                      <span
                        className={`font-semibold tabular-nums ${
                          transaction.amount > 0 ? 'text-[#4ADE80]' : 'text-[#FCA5A5]'
                        }`}
                      >
                        {transaction.amount > 0 ? '+' : ''}€{transaction.amount.toFixed(2)}
                      </span>
                    </td>
                    <td className={admin.td}>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(transaction.status)}
                        <span className={statusBadgeClass(transaction.status)}>{transaction.status}</span>
                      </div>
                    </td>
                    <td className={`${admin.td} max-w-[220px] truncate ${admin.tdMuted}`}>
                      {transaction.description || '—'}
                    </td>
                    <td className={`${admin.td} whitespace-nowrap ${admin.tdMuted}`}>
                      {new Date(transaction.createdAt).toLocaleString()}
                    </td>
                    <td className={admin.td}>
                      <button
                        type="button"
                        onClick={() => handleViewDetails(transaction)}
                        className={`${admin.btnPrimary} py-1.5 px-3 text-xs`}
                      >
                        <FiEye size={14} />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={pagination.page}
            pageSize={pagination.limit}
            total={pagination.total}
            onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
          />
        </div>
      </div>
    </Layout>
  );
};

export default AdminTransactions;
